import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Particles } from '../components/Particles.tsx';
import { 
  Box, Typography, Button, useMediaQuery, useTheme, keyframes, CircularProgress 
} from '@mui/material';

// --- ANIMÁCIÓK ÉS GIF ADATOK ---
const fadeCycle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0.8) rotate(-5deg); }
  50% { opacity: 0.8; transform: scale(1) rotate(5deg); }
`;

const GIF_DATA = {
    question: ["15502118324612616342", "12053814847638028030", "25676985", "26097141", "24985324", "24985048"],
    angry: ["20195177", "18330252195217648656", "21681908", "14967220799009428095", "9323741479407494404"],
    love: ["481671407995970679", "21458020", "21655580", "15123231842779869542", "20721043"]
};

interface ValentineData {
    p1: { q: string; yes: string; no: string };
    p2: { q: string; yes: string; no: string };
    p3: { q: string; yes: string; no: string };
    p4: { lastTitle: string };
}

const ValentineCustomPage: React.FC = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const cardId = queryParams.get('id'); // Feltételezve, hogy ?id=... formátumú a link

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // --- Állapotok ---
    const [loading, setLoading] = useState(true);
    const [cardData, setCardData] = useState<ValentineData | null>(null);
    const [phase, setPhase] = useState<'question' | 'angry' | 'love'>('question');
    const [step, setStep] = useState(0);
    const [noBtnPos, setNoBtnPos] = useState({ left: '60%', top: '60%' });
    const [yesBtnPos, setYesBtnPos] = useState({ left: '30%', top: '60%' });
    const [floatingGifs, setFloatingGifs] = useState<{ id: string, x: number, y: number, key: number }[]>([]);
    
    const noBtnRef = useRef<HTMLButtonElement>(null);
    const angryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // --- ADATOK LEKÉRÉSE FETCH SEGÍTSÉGÉVEL ---
    useEffect(() => {
        const fetchValentineData = async () => {
            if (!cardId) {
                setLoading(false);
                return;
            }
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/user/public/valentine/${cardId}`, {
                    headers: { 
                        'Content-Type': 'application/json'
                    },
                });
                
                if (!response.ok) throw new Error('Failed to fetch');
                
                const data = await response.json();
                // Feltételezve, hogy a backend a "card" objektumban küldi az adatokat
                setCardData(data.card); 
            } catch (error) {
                console.error("Error fetching valentine data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchValentineData();
    }, [cardId]);

    // --- TENOR ÉS GIF LOGIKA ---
    useEffect(() => {
        const script = document.createElement("script");
        script.src = "https://tenor.com/embed.js";
        script.async = true;
        document.body.appendChild(script);
        return () => { 
            const existingScript = document.querySelector('script[src*="tenor.com/embed.js"]');
            if (existingScript) document.body.removeChild(existingScript);
        };
    }, [phase, floatingGifs]);

    const generateGifs = useCallback((currentPhase: 'question' | 'angry' | 'love') => {
        const library = GIF_DATA[currentPhase];
        const gifCount = isMobile ? 4 : 8;
        const size = isMobile ? 120 : 180;

        const newGifs = Array.from({ length: gifCount }).map(() => ({
            id: library[Math.floor(Math.random() * library.length)],
            x: Math.random() * (window.innerWidth - size - 40) + 20,
            y: Math.random() * (window.innerHeight - size - 100) + 50,
            key: Math.random()
        }));
        setFloatingGifs(newGifs);
    }, [isMobile]);

    useEffect(() => {
        if (!loading && cardData) {
            generateGifs(phase);
            const interval = setInterval(() => generateGifs(phase), 8000);
            return () => clearInterval(interval);
        }
    }, [loading, cardData, phase, generateGifs]);

    // --- INTERAKCIÓK ---
    const handleNoMove = useCallback(() => {
        if (phase === 'love') return;
        if (angryTimeoutRef.current) clearTimeout(angryTimeoutRef.current);
        
        const btnWidth = isMobile ? 110 : 140;
        const nx = Math.random() * (window.innerWidth - btnWidth - 40) + 20;
        const ny = Math.random() * (window.innerHeight - 100 - 150) + 150;

        setNoBtnPos({ left: `${nx}px`, top: `${ny}px` });
        setPhase('angry');
        angryTimeoutRef.current = setTimeout(() => setPhase('question'), 3000);
    }, [phase, isMobile]);

    const handleYesClick = () => {
        if (angryTimeoutRef.current) clearTimeout(angryTimeoutRef.current);
        if (step < 2) {
            setStep(prev => prev + 1);
            setPhase('question');
        } else {
            setPhase('love');
        }
    };

    if (loading) return (
        <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff0f3' }}>
            <CircularProgress sx={{ color: '#ff4d6d' }} />
        </Box>
    );

    if (!cardData) return (
        <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h5" color="#ff4d6d">Kártya nem található vagy hiba történt.</Typography>
        </Box>
    );

    // Dinamikus kulcs meghatározása (p1, p2, p3)
    const currentPhaseKey = `p${step + 1}` as keyof Pick<ValentineData, 'p1' | 'p2' | 'p3'>;
    const currentTexts = cardData[currentPhaseKey];

    return (
        <Box sx={{ position: 'relative', minHeight: '100vh', width: '100vw', overflow: 'hidden', background: 'linear-gradient(to bottom, #fff0f3, #ffccd5)' }}>
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 0 }}><Particles /></Box>

            {/* GIF RÉTEG */}
            <Box sx={{ position: 'fixed', inset: 0, zIndex: 10, pointerEvents: 'none' }}>
                {floatingGifs.map((gif) => (
                    <Box key={gif.key} className="tenor-gif-embed" data-postid={gif.id} data-share-method="host" data-aspect-ratio="1" 
                        sx={{ position: 'absolute', left: `${gif.x}px`, top: `${gif.y}px`, width: isMobile ? '120px' : '180px', animation: `${fadeCycle} 4s infinite ease-in-out`, borderRadius: '15px', overflow: 'hidden' }} 
                    />
                ))}
            </Box>

            {/* TARTALOM */}
            <Box sx={{ position: 'relative', zIndex: 50, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', pt: '25vh' }}>
                <Typography variant="h2" sx={{ fontSize: isMobile ? '1.8rem' : '3.5rem', color: '#ff4d6d', fontWeight: 'bold', maxWidth: '90%', fontFamily: "'Dancing Script', cursive", textShadow: '1px 1px 2px rgba(0,0,0,0.1)' }}>
                    {phase === 'love' ? cardData.p4.lastTitle : currentTexts.q}
                </Typography>

                {phase !== 'love' && (
                    <>
                        <Button 
                            onClick={handleYesClick} 
                            sx={{ 
                                position: 'fixed', left: yesBtnPos.left, top: yesBtnPos.top, 
                                bgcolor: '#ff4d6d', color: 'white', borderRadius: '50px', 
                                px: isMobile ? 3 : 5, py: 1.5, fontWeight: 'bold', zIndex: 60,
                                '&:hover': { bgcolor: '#ff758f' }
                            }}
                        >
                            {currentTexts.yes}
                        </Button>

                        <Button 
                            ref={noBtnRef} 
                            onMouseEnter={handleNoMove} 
                            sx={{ 
                                position: 'fixed', left: noBtnPos.left, top: noBtnPos.top, 
                                bgcolor: '#444', color: 'white', borderRadius: '50px', 
                                px: isMobile ? 3 : 5, py: 1.5, fontWeight: 'bold', zIndex: 59,
                                transition: 'all 0.4s cubic-bezier(0.15, 0.85, 0.25, 1)'
                            }}
                        >
                            {currentTexts.no}
                        </Button>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default ValentineCustomPage;